/**
 * Auto-stop de la grabación (F1: «detección de voz o 4 s», master §2.1).
 *
 * La política de corte es una función PURA (`debeCortar`) para poder testearla
 * sin WebAudio; `medirNivel` es el único trozo que toca el navegador. Cortar
 * solo por tiempo mutilaría sílabas lentas, y cortar solo por silencio dejaría
 * la grabación abierta si el niño no habla: hacen falta las dos condiciones.
 */

export interface EstadoVoz {
  /** ¿Se detectó voz en algún momento de esta grabación? */
  vozDetectada: boolean;
  /** ms de silencio continuo desde la última voz. */
  msSilencio: number;
  /** ms totales grabando. */
  msTotal: number;
}

export interface ConfigVoz {
  /** Corte duro (F1: 4000 ms). */
  maxMs: number;
  /** Silencio tras la voz que da la emisión por terminada. */
  silencioFinalMs: number;
}

export const CONFIG_VOZ_F1: ConfigVoz = { maxMs: 4_000, silencioFinalMs: 700 };

/** Umbral RMS (0-1) por encima del cual consideramos que hay voz. */
export const UMBRAL_VOZ = 0.02;

/** ¿Toca cerrar la grabación? Por silencio tras la voz, o por tiempo máximo. */
export function debeCortar(e: EstadoVoz, cfg: ConfigVoz): boolean {
  if (e.msTotal >= cfg.maxMs) return true;
  return e.vozDetectada && e.msSilencio >= cfg.silencioFinalMs;
}

/** Avanza el estado de detección con la medición de un frame. */
export function siguienteEstado(e: EstadoVoz, nivel: number, dtMs: number): EstadoVoz {
  const hayVoz = nivel >= UMBRAL_VOZ;
  return {
    vozDetectada: e.vozDetectada || hayVoz,
    msSilencio: hayVoz ? 0 : e.msSilencio + dtMs,
    msTotal: e.msTotal + dtMs,
  };
}

export const ESTADO_VOZ_INICIAL: EstadoVoz = { vozDetectada: false, msSilencio: 0, msTotal: 0 };

/** RMS normalizado (0-1) del buffer de tiempo del analizador. */
export function medirNivel(analyser: AnalyserNode): number {
  const buf = new Float32Array(analyser.fftSize);
  analyser.getFloatTimeDomainData(buf);
  let suma = 0;
  for (const v of buf) suma += v * v;
  return Math.sqrt(suma / buf.length);
}
